<template>
  <div>
    <el-dropdown
      ref="googleAccountDropDown"
      trigger="click"
      placement="bottom-start"
      style="margin-left: 30px"
      v-loading="loading"
      @visible-change="showDropdown"
    >
      <div class="account-wrapper">
        <div class="pr flex relative">
          <i class="el-icon-info"></i>
          <p
            :class="[
              'f-circle',
              'absolute',
              'left-[20px]',
              'ml[-4px]',
              currentAccountInfo && currentAccountInfo.available ? 'bg-success' : 'bg-danger',
            ]"
          ></p>
        </div>
        <el-tooltip
          :content="currentAccountName"
          placement="right"
          effect="light"
        >
          <p
            class="ellipsis-1"
            style="line-height: initial"
          >
            <span
              v-if="currentAccountInfo && currentAccountInfo.isRefuseLoginAcct === '1'"
              class="text-red-500 text-xs mr-1"
              >【有拒登】</span
            >{{ currentAccountName }}
          </p>
        </el-tooltip>
        <el-tooltip
          v-if="isViolated(currentAccountInfo)"
          content="代理标记违规不可充值"
        >
          <span class="violation-tag">违规</span>
        </el-tooltip>
        <i class="el-icon-caret-bottom"></i>
      </div>
      <el-dropdown-menu slot="dropdown">
        <div class="w-[960px] h-96 flex flex-col relative">
          <!-- 模式切换 -->
          <div class="bg-white border-b border-gray-200 flex-shrink-0">
            <div class="flex items-center justify-between p-3">
              <div
                class="flex items-center cursor-pointer"
                @click="toggleMode"
              >
                <i
                  class="el-icon-sort text-blue-500 mr-2"
                  style="transform: rotate(90deg)"
                ></i>

                <span class="text-sm font-medium">{{ currentMode === 'mcc' ? 'MCC模式' : '传统模式' }}</span>
              </div>
              <div class="flex items-center justify-end">
                <span class="mr-2">
                  <i
                    class="el-icon-refresh cursor-pointer text-blue-500 mr-2"
                    @click="refreshData"
                  ></i>
                  <span class="text-[12px]">同步账户</span>
                </span>
                <el-tabs
                  v-model="available"
                  @tab-click="handleClick"
                  :before-leave="beforeTabLeave"
                  type="card"
                  v-if="currentMode === 'traditional'"
                  :class="{ 'tabs-disabled': traditionalLoading }"
                >
                  <el-tab-pane
                    :label="`活跃账户(${accountStats.validCount || 0})`"
                    name="1"
                  ></el-tab-pane>
                  <el-tab-pane name="2">
                    <template slot="label">
                      <span>
                        {{ `封禁账户(${accountStats.invalidCount || 0})` }}
                      </span>
                      <el-tooltip
                        effect="dark"
                        content="被封和被标记禁用的账户"
                        placement="top"
                      >
                        <i class="el-icon-info"></i>
                      </el-tooltip>
                    </template>
                  </el-tab-pane>
                </el-tabs>
              </div>
            </div>
            <div class="p-2">
              <el-input
                v-model="keyword"
                placeholder="搜索账户名称ID"
                size="mini"
                prefix-icon="el-icon-search"
                :suffix-icon="mccSearchLoading && currentMode === 'mcc' ? 'el-icon-loading' : ''"
                @input="handleSearch"
                @keydown.native.enter="handleSearch"
                @blur="handleSearch"
              />
            </div>
          </div>

          <!-- MCC模式 -->
          <div
            v-show="currentMode === 'mcc'"
            class="flex-1 overflow-hidden flex flex-col"
          >
            <el-tree
              ref="mccTree"
              :data="filteredMccAccounts"
              :props="treeProps"
              node-key="rowKey"
              :expand-on-click-node="false"
              :highlight-current="true"
              :load="getMcc"
              lazy
              @node-click="selectAccount"
              class="flex-1 overflow-y-auto"
            >
              <div
                class="w-full text-sm break-all mb-2 p-2 rounded transition-all border-transparent"
                :class="{
                  'bg-blue-50 border-blue-400 shadow-md': data.accountId == currentAccountInfo.accountId,
                  'hover:bg-gray-50 hover:shadow-sm': data.accountId != currentAccountInfo.accountId,
                }"
                slot-scope="{ node, data }"
              >
                <div class="flex justify-between items-start">
                  <div class="flex items-center">
                    <el-tooltip
                      v-if="!data.canManagerClient"
                      :disabled="data.available"
                      class="item"
                      effect="dark"
                      :content="data.prompt"
                      placement="top-end"
                    >
                      <i
                        :class="getStatusIcon(data.available)"
                        class="mr-2"
                      ></i>
                    </el-tooltip>
                    <el-tooltip
                      v-if="data.error"
                      effect="dark"
                      :content="data.error"
                      placement="top"
                    >
                      <i class="mr-2 text-sm el-icon-warning"></i>
                    </el-tooltip>
                    <el-tooltip
                      :content="data.accountName"
                      placement="top"
                      effect="dark"
                    >
                      <span
                        class="text-[14px] font-bold truncate flex-1 transition-colors"
                        :class="
                          data.accountId == currentAccountInfo.accountId
                            ? 'text-blue-600 font-semibold'
                            : 'text-gray-800'
                        "
                      >
                        <span
                          v-if="data.isRefuseLoginAcct === '1'"
                          class="text-red-500 text-xs mr-1 font-normal"
                          >【有拒登】</span
                        >{{ data.accountName }}
                      </span>
                    </el-tooltip>
                    <el-tooltip
                      v-if="isViolated(data)"
                      content="代理标记违规不可充值"
                    >
                      <span class="violation-tag">违规</span>
                    </el-tooltip>
                  </div>
                  <div
                    v-if="!data.canManagerClient"
                    class="flex-shrink-0"
                  >
                    <span
                      class="text-blue-500 text-xs cursor-pointer hover:underline"
                      @click.stop="openInNewWindow(data)"
                    >
                      新窗口打开
                    </span>
                    <span
                      v-if="data.available"
                      class="text-blue-500 text-xs cursor-pointer hover:underline ml-2"
                      @click.stop="openRecharge(data)"
                    >
                      账户充值
                    </span>
                  </div>
                </div>

                <div
                  class="flex items-center text-xs text-[#999] mt-2"
                  v-if="!data.canManagerClient"
                >
                  <span class="mr-2">账户ID: {{ data.accountId }}</span>
                  <span
                    >账户余额:
                    <span class="text-red-500 font-medium mr-2"
                      >{{ formatCurrency(data.balance) }} {{ data.currency }}</span
                    ></span
                  >
                  <!-- <span
                    >当日消耗:
                    <span class="text-red-500 font-medium"
                      >{{ formatCurrency(data.dailySpend) }} {{ data.currency }}</span
                    ></span
                  > -->
                </div>
                <div
                  v-else
                  class="flex items-center text-xs text-[#999] mt-2"
                >
                  <span class="mr-2">账户ID: {{ data.accountId }}</span>
                  <span>经理</span>
                </div>
              </div>
            </el-tree>
          </div>

          <!-- 传统模式 -->
          <div
            v-show="currentMode === 'traditional'"
            class="overflow-hidden flex flex-col"
          >
            <div
              class="flex-1 overflow-y-auto p-2 grid grid-cols-2 gap-2"
              @scroll="handleTraditionalScroll"
              v-loading="traditionalLoading && traditionalAccounts.length === 0"
              ref="traditionalScrollContainer"
            >
              <div
                v-for="item in filteredTraditionalAccounts"
                :key="item.accountId"
                class="rounded h-20 p-2 cursor-pointer transition-all hover:bg-gray-50 hover:border-blue-400 border border-transparent flex flex-col justify-between"
                :class="{
                  'bg-blue-50 border-blue-400 shadow-md': item.accountId == currentAccountInfo.accountId,
                  'hover:shadow-sm': item.accountId != currentAccountInfo.accountId,
                }"
                @click="selectAccount(item)"
              >
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center flex-1 overflow-hidden">
                    <i
                      :class="getStatusIcon(item.available)"
                      class="mr-2 text-sm"
                    ></i>
                    <el-tooltip
                      v-if="item.error"
                      effect="dark"
                      :content="item.error"
                      placement="top"
                    >
                      <i class="mr-2 text-sm el-icon-warning"></i>
                    </el-tooltip>
                    <el-tooltip
                      :content="item.accountName"
                      placement="top"
                      effect="dark"
                    >
                      <span
                        class="font-medium text-[14px] truncate flex-1 mr-2 transition-colors"
                        :class="
                          item.accountId == currentAccountInfo.accountId
                            ? 'text-blue-600 font-semibold'
                            : 'text-gray-800'
                        "
                        ><span
                          v-if="item.isRefuseLoginAcct === '1'"
                          class="text-red-500 text-xs mr-1 font-normal"
                          >【有拒登】</span
                        >{{ item.accountName }}</span
                      >
                    </el-tooltip>
                    <el-tooltip
                      v-if="isViolated(item)"
                      content="代理标记违规不可充值"
                    >
                      <span class="violation-tag">违规</span>
                    </el-tooltip>
                  </div>
                  <div class="text-right flex-shrink-0">
                    <span
                      class="text-blue-500 text-xs cursor-pointer hover:underline"
                      @click.stop="openInNewWindow(item)"
                      >新窗口打开</span
                    >
                    <span
                      v-if="item.available"
                      class="text-blue-500 text-xs cursor-pointer hover:underline ml-2"
                      @click.stop="openRecharge(item)"
                      >账户充值</span
                    >
                  </div>
                </div>
                <div class="flex items-center justify-start">
                  <div class="text-xs mr-2 text-gray-600">账户ID: {{ item.accountId }}</div>
                  <div class="flex justify-between text-xs mr-2">
                    <span class="text-gray-600">账户余额:</span>
                    <span class="font-medium text-red-500">{{ formatCurrency(item.balance) }} USD</span>
                  </div>
                  <div class="flex justify-between text-xs">
                    <span class="text-gray-600">当日消费:</span>
                    <span class="font-medium text-red-500">{{ formatCurrency(item.spend) }} USD</span>
                  </div>
                </div>
              </div>

              <!-- 滚动加载提示 -->
              <div
                v-if="traditionalLoading && traditionalAccounts.length > 0"
                class="col-span-2 text-center py-1 text-gray-500"
              >
                滚动加载中...
              </div>
            </div>
          </div>

          <div
            v-if="noSearchData"
            class="absolute inset-0 top-28 flex items-center justify-center text-gray-500"
          >
            无匹配数据
          </div>
        </div>
      </el-dropdown-menu>
    </el-dropdown>
    <google-account-recharge
      :dialog-visible="showRecharge"
      :account="rechargeAccount"
      @close="closeRecharge"
    ></google-account-recharge>
  </div>
</template>

<script>
import { accountMccPages, accountList, accountCount, findAccountById, credentialList } from '@/api/google/account';
import GoogleAccountRecharge from '@/components/google/googleAccountRecharge.vue';

export default {
  name: 'GoogleAccount',
  components: {
    GoogleAccountRecharge,
  },
  data: () => ({
    showRecharge: false,
    rechargeAccount: null,
    currentMode: 'mcc',
    mccAccounts: [],
    traditionalAccounts: [],
    accountStats: {},
    loading: false,
    noSearchData: false,
    searchTimer: null,
    lastSearchKeyword: '', // 记录上次搜索的关键词
    mccSearchLoading: false, // MCC搜索加载状态
    currentAccountInfo: {},
    traditionalPage: 1,
    traditionalPageSize: 20,
    traditionalHasMore: true,
    traditionalLoading: false,
    mccTreeKey: 0,
    treeProps: {
      children: 'zones',
      label: 'name',
      isLeaf: 'canNotManagerClient',
    },
    available: '1',
    keyword: '',
    mccKeyword: '', // MCC模式的搜索关键词
    traditionalKeyword: '', // 传统模式的搜索关键词
    // 缓存不同tab的数据
    traditionalAccountsCache: {
      1: [], // 活跃账户
      2: [], // 封禁账户
    },
    traditionalPageCache: {
      1: 1,
      2: 1,
    },
    traditionalHasMoreCache: {
      1: true,
      2: true,
    },
  }),
  computed: {
    currentAccountName() {
      return this.currentAccountInfo?.name || this.currentAccountInfo?.accountName || '请选择Google账户';
    },
    filteredMccAccounts: (vm) => vm.mccAccounts,
    filteredTraditionalAccounts: (vm) => vm.traditionalAccounts,
  },
  methods: {
    showDropdown(visible) {
      if (!visible) return;
      this.loadAccountStats();
      if (this.currentMode === 'mcc' && !this.mccAccounts.length && !this.keyword && !this.mccSearchLoading) {
        // 只有在没有搜索关键词、没有数据、没有正在搜索时才加载
        this.loadMccData();
      } else if (this.currentMode === 'traditional' && !this.traditionalAccounts.length) {
        this.loadTraditionalData();
      }
    },
    // 处理凭证切换事件
    async handleCredentialChanged({ credentialUserId, accountId }) {
      // 标记正在处理凭证切换，防止 watch 重复触发
      this._isCredentialChanging = true;

      // 只有在真正切换凭证时才清空account_id
      if (this.$route.query.credentialUserId && this.$route.query.credentialUserId != credentialUserId) {
        this.$router.push({
          ...this.$route,
          query: {
            ...this.$route.query,
            account_id: undefined,
          },
        });
      }

      // 清空现有数据和缓存
      this.mccAccounts = [];
      this.traditionalAccounts = [];
      this.accountStats = {};
      this.mccTreeKey++;
      this.keyword = '';
      this.mccKeyword = '';
      this.traditionalKeyword = '';
      this.lastSearchKeyword = '';
      this.mccSearchLoading = false;

      // 清空缓存
      this.traditionalAccountsCache = { 1: [], 2: [] };
      this.traditionalPageCache = { 1: 1, 2: 1 };
      this.traditionalHasMoreCache = { 1: true, 2: true };

      // 清空当前账户信息
      this.currentAccountInfo = {};

      // 切换到传统模式并加载数据
      this.currentMode = 'traditional';
      this.available = '1';

      // 选择账户：优先路由参数，其次localStorage，最后默认逻辑
      await this.selectDefaultAccount();

      this.$nextTick(() => {
        this._isCredentialChanging = false;
      });
    },
    // 搜索并选择账户
    async searchAndSelectAccount(accountId) {
      let foundAccount = null;

      // 在当前模式下搜索账户
      if (this.currentMode === 'mcc') {
        foundAccount = this.mccAccounts.find((acc) => acc.accountId == accountId);
      } else {
        foundAccount = this.traditionalAccounts.find((acc) => acc.accountId == accountId);
      }

      if (foundAccount) {
        // 找到账户，选择它
        this.selectAccount(foundAccount);
        // 高亮显示
        this.highlightAccount(foundAccount);
        // 清空搜索关键词
        // this.keyword = '';
      } else {
        // 如果在当前模式下未找到，尝试切换模式再搜索
        const otherMode = this.currentMode === 'mcc' ? 'traditional' : 'mcc';
        this.currentMode = otherMode;

        // 加载另一个模式的数据
        if (otherMode === 'mcc') {
          await this.loadMccData();
          foundAccount = this.mccAccounts.find((acc) => acc.accountId == accountId);
        } else {
          await this.loadTraditionalData();
          foundAccount = this.traditionalAccounts.find((acc) => acc.accountId == accountId);
        }
        console.log(foundAccount, foundAccount, 'foundAccountfoundAccount');
        if (foundAccount) {
          this.selectAccount(foundAccount);
          this.highlightAccount(foundAccount);
          //   this.keyword = '';
        } else {
          // 两个模式都未找到，显示确认对话框
          this.$confirm('该凭证下未找到对应的账户，是否前往资产同步账户？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          })
            .then(() => {
              this.$message.info('请前往资产同步页面同步账户');
            })
            .catch(() => {
              // 用户取消
            });
        }
      }
    },
    highlightAccount() {
      // 高亮逻辑已通过模板实现
    },
    async loadCurrentAccountInfo() {
      const accountId = this.$route.query.account_id || localStorage.getItem('googleAccountId');
      if (!accountId || accountId === this.currentAccountInfo?.accountId) return;

      try {
        const { code, data } = await findAccountById({
          accountId,
          credentialUserId: this.$route.query.credentialUserId,
        });
        if (code === 0) this.currentAccountInfo = data;
      } catch (error) {
        console.error('获取账户信息失败:', error);
      }
    },
    toggleMode() {
      this.currentMode = this.currentMode === 'mcc' ? 'traditional' : 'mcc';

      // 切换模式时恢复对应模式的搜索关键词
      if (this.currentMode === 'mcc') {
        this.keyword = this.mccKeyword;
      } else {
        this.keyword = this.traditionalKeyword;
      }

      this.lastSearchKeyword = ''; // 重置搜索关键词记录

      if (this.currentMode === 'mcc' && !this.mccAccounts.length) {
        this.loadMccData();
      } else if (this.currentMode === 'traditional' && !this.traditionalAccounts.length) {
        this.loadTraditionalData();
      }
    },
    async loadData() {
      this.loading = true;
      try {
        if (this.currentMode === 'mcc') {
          await this.loadMccData();
        } else {
          await Promise.all([this.loadTraditionalData()]);
        }
      } catch (error) {
        console.error('加载数据失败:', error);
        this.$message.error('加载数据失败');
      } finally {
        this.loading = false;
      }
    },
    async loadMccData() {
      // 避免重复搜索相同关键词
      if (this.mccSearchLoading) {
        return;
      }

      // 如果关键词相同且不是初始加载，直接返回
      if (this.lastSearchKeyword === this.keyword && this.mccAccounts.length > 0) {
        return;
      }

      this.mccSearchLoading = true;
      this.lastSearchKeyword = this.keyword;

      try {
        const params = {
          keyword: this.keyword,
          pageSize: 200,
          page: 1,
          searchDetail: false,
          parentMccid: 0,
          credentialUserId: this.$route.query.credentialUserId || 0,
        };
        const res = await accountMccPages(params);
        if (res.code === 0) {
          const accountIds = new Set();
          this.mccAccounts = (res.data?.list || [])
            .filter((item) => {
              if (accountIds.has(item.accountId)) {
                return false;
              }
              accountIds.add(item.accountId);
              return true;
            })
            .map((item, index) => ({
              ...item,
              id: item.accountId,
              canNotManagerClient: !item.canManagerClient,
              rowKey: `${item.credentialId}-${item.accountId}-${index}`,
            }));
          this.mccTreeKey++;
        }
      } finally {
        this.mccSearchLoading = false;
      }
    },
    async loadTraditionalData(isLoadMore = false) {
      if (this.traditionalLoading) return;

      if (!isLoadMore) {
        this.traditionalPage = 1;
        this.traditionalAccounts = [];
        this.traditionalHasMore = true;
      }

      if (!this.traditionalHasMore) return;

      this.traditionalLoading = true;
      try {
        const params = {
          page_number: this.traditionalPage,
          page_size: this.traditionalPageSize,
          orderBy: 'spend',
          keyword: this.keyword,
          available: !!(this.available === '1'),
          credentialUserId: this.$route.query.credentialUserId || 0,
        };
        const res = await accountList(params);
        if (res.code === 0) {
          const newAccounts = res.data?.list || [];

          if (isLoadMore) {
            this.traditionalAccounts.push(...newAccounts);
          } else {
            this.traditionalAccounts = newAccounts;
          }

          // 更新缓存
          this.traditionalAccountsCache[this.available] = [...this.traditionalAccounts];
          this.traditionalPageCache[this.available] = this.traditionalPage + 1;
          this.traditionalHasMoreCache[this.available] = newAccounts.length === this.traditionalPageSize;

          console.log(this.traditionalAccounts, 'traditionalAccounts');
          this.traditionalHasMore = newAccounts.length === this.traditionalPageSize;
          this.traditionalPage++;
        }
      } finally {
        this.traditionalLoading = false;
      }
    },
    // 选择默认账户
    async selectDefaultAccount() {
      const targetAccountId = this.$route.query.account_id || localStorage.getItem('googleAccountId');
      let defaultAccount = null;

      if (targetAccountId) {
        // 先从缓存中查找指定账户
        defaultAccount =
          this.traditionalAccountsCache['1'].find((acc) => acc.accountId == targetAccountId) ||
          this.traditionalAccountsCache['2'].find((acc) => acc.accountId == targetAccountId);

        if (!defaultAccount) {
          // 缓存中没有，并行加载活跃和封禁账户
          this.available = '1';
          await this.loadTraditionalData();
          defaultAccount = this.traditionalAccounts.find((acc) => acc.accountId == targetAccountId);

          if (!defaultAccount) {
            this.available = '2';
            await this.loadTraditionalData();
            defaultAccount = this.traditionalAccounts.find((acc) => acc.accountId == targetAccountId);
          }
        } else {
          // 找到账户，设置对应的tab和数据
          this.available = defaultAccount.available ? '1' : '2';
          this.traditionalAccounts = this.traditionalAccountsCache[this.available];
        }
      }

      // 如果没有找到指定账户，使用默认逻辑
      if (!defaultAccount) {
        // 确保活跃账户已加载
        if (this.traditionalAccountsCache['1'].length === 0) {
          this.available = '1';
          await this.loadTraditionalData();
        }
        defaultAccount = (
          this.traditionalAccountsCache['1'].length > 0 ? this.traditionalAccountsCache['1'] : this.traditionalAccounts
        ).find((acc) => acc.available);

        if (defaultAccount) {
          this.available = '1';
          this.traditionalAccounts =
            this.traditionalAccountsCache['1'].length > 0
              ? this.traditionalAccountsCache['1']
              : this.traditionalAccounts;
        }
      }

      if (defaultAccount) {
        this.currentAccountInfo = {
          id: defaultAccount.accountId,
          name: defaultAccount.accountName,
          accountName: defaultAccount.accountName,
          accountId: defaultAccount.accountId,
          available: defaultAccount.available,
          isRefuseLoginAcct: defaultAccount.isRefuseLoginAcct,
          complianceStatus: defaultAccount.complianceStatus,
        };

        this.$nextTick(() => {
          this.$router.push({
            ...this.$route,
            query: {
              ...this.$route.query,
              account_id: defaultAccount.accountId,
            },
          });
        });

        this.$store.commit('initData/setAccountIdGoogle', defaultAccount.accountId);
        localStorage.setItem('googleAccountId', defaultAccount.accountId);
        localStorage.setItem('googleCredentialUserId', this.$route.query.credentialUserId);
        this.$emit('account-selected', defaultAccount);
      } else {
        this.$confirm('当前凭证没有账户，请去资产里面同步账户', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        });
      }
    },
    // 加载中阻止 tab 切换，返回 false 会阻止 el-tabs 切换
    beforeTabLeave() {
      if (this.traditionalLoading) return false;
    },
    handleClick(tab) {
      // 加载中禁止切换 tab（CSS 层已阻止点击，这里做兜底）
      if (this.traditionalLoading) return;

      this.available = tab.name;

      // 检查是否已有缓存数据，没有才加载
      if (this.traditionalAccountsCache[tab.name].length === 0) {
        this.loadTraditionalData();
      } else {
        // 使用缓存数据
        this.traditionalAccounts = this.traditionalAccountsCache[tab.name];
        this.traditionalPage = this.traditionalPageCache[tab.name];
        this.traditionalHasMore = this.traditionalHasMoreCache[tab.name];
      }
    },
    async loadAccountStats() {
      const res = await accountCount({ credentialUserId: this.$route.query.credentialUserId || 0 });
      if (res.code === 0) {
        this.accountStats = res.data;
      }
    },
    handleSearch() {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(() => {
        if (this.currentMode === 'mcc') {
          this.loadMccData();
        } else {
          // 搜索时清空缓存，重新加载
          this.traditionalAccountsCache[this.available] = [];
          this.traditionalPageCache[this.available] = 1;
          this.traditionalHasMoreCache[this.available] = true;
          this.loadTraditionalData();
        }
      }, 800); // 增加防抖时间到800ms
    },
    refreshData() {
      // 重置搜索状态
      this.lastSearchKeyword = '';
      this.mccSearchLoading = false;
      this.loadData();
    },
    selectAccount(account) {
      console.log('选择账户:', account);

      if (this.currentMode === 'mcc') {
        // MCC模式下只能选择叶子节点（不能管理客户的账户）
        if (account.canNotManagerClient) {
          this.currentAccountInfo = {
            id: account.accountId,
            name: account.accountName,
            accountName: account.accountName,
            accountId: account.accountId,
            available: account.available,
            isRefuseLoginAcct: account.isRefuseLoginAcct,
            complianceStatus: account.complianceStatus,
          };

          this.$router.push({
            ...this.$route,
            query: {
              ...this.$route.query,
              account_id: account.accountId,
            },
          });

          this.$store.commit('initData/setAccountIdGoogle', account.accountId);
          localStorage.setItem('googleAccountId', account.accountId);
          localStorage.setItem('googleCredentialUserId', this.$route.query.credentialUserId);

          // 关闭下拉框
          this.$refs.googleAccountDropDown.visible = false;

          this.$emit('account-selected', account);
        }
      } else {
        this.currentAccountInfo = {
          id: account.accountId,
          name: account.accountName,
          accountName: account.accountName,
          accountId: account.accountId,
          available: account.available,
          isRefuseLoginAcct: account.isRefuseLoginAcct,
          complianceStatus: account.complianceStatus,
        };

        this.$router.push({
          ...this.$route,
          query: {
            ...this.$route.query,
            account_id: account.accountId,
          },
        });

        this.$store.commit('initData/setAccountIdGoogle', account.accountId);
        localStorage.setItem('googleAccountId', account.accountId);
        localStorage.setItem('googleCredentialUserId', this.$route.query.credentialUserId);
        this.$store.commit('google/setCreateAdAccount', account);

        this.$refs.googleAccountDropDown.visible = false;
        this.$emit('account-selected', account);
      }
    },
    openRecharge(account) {
      this.rechargeAccount = account || {};
      if (this.$refs.googleAccountDropDown) {
        this.$refs.googleAccountDropDown.visible = false;
      }
      this.showRecharge = true;
    },
    closeRecharge() {
      this.showRecharge = false;
      this.rechargeAccount = null;
    },
    openInNewWindow(account) {
      const query = {
        account_id: account.accountId || account.customerId,
      };

      if (this.currentMode === 'mcc' && account.rowKey) {
        const credentialId = account.rowKey.split('-')[0];
        query.credentialId = credentialId;
      }

      const { href } = this.$router.resolve({
        path: '/google/adManagement',
        query,
      });
      window.open(href, '_blank');
    },
    getStatusClass(status) {
      if (status === 'ENABLED' || status === 1) {
        return 'bg-success';
      }
      return 'bg-danger';
    },
    getStatusIcon(status) {
      if (status) {
        return 'el-icon-success status-icon success text-success';
      }
      return 'el-icon-error status-icon error text-danger';
    },
    getStatusText(status) {
      if (status) {
        return '正常';
      }
      return '封禁';
    },
    formatCurrency: (value) => (value ? parseFloat(value).toFixed(2) : '0.00'),
    isViolated(account) {
      if (!account || account.canManagerClient) {
        return false;
      }
      return Number(account.complianceStatus) === 3;
    },
    filterTreeData(data, keyword) {
      if (!keyword) return data;
      return data.filter(
        (item) =>
          item.name?.toLowerCase().includes(keyword.toLowerCase()) || item.accountId?.toString().includes(keyword),
      );
    },
    handleTraditionalScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollTop + clientHeight >= scrollHeight - 10 && this.traditionalHasMore && !this.traditionalLoading) {
        this.loadTraditionalData(true);
      }
    },
    getMcc(node, resolve) {
      // 如果正在搜索或有搜索关键词，不执行懒加载
      if (this.keyword || this.mccSearchLoading) {
        resolve([]);
        return;
      }

      let parentMccid = node.level === 0 ? 0 : node.key.split('-')[1];
      const params = {
        page: 1,
        parentMccid: parentMccid,
        pageSize: 200,
        searchDetail: false,
        credentialUserId: this.$route.query.credentialUserId,
      };
      accountMccPages(params).then((res) => {
        const accountIds = new Set();
        const newData = res.data.list
          ? res.data.list
              .filter((item) => {
                if (accountIds.has(item.accountId)) {
                  return false;
                }
                accountIds.add(item.accountId);
                return true;
              })
              .map((item, index) => ({
                ...item,
                id: item.accountId,
                canNotManagerClient: !item.canManagerClient,
                rowKey: `${item.credentialId}-${item.accountId}-${parentMccid}-${index}`,
              }))
          : [];
        resolve(newData);
      });
    },
  },
  created() {
    // this.loadData();
    this.loadCurrentAccountInfo();
  },
  watch: {
    keyword(newVal) {
      // 根据当前模式更新对应的关键词
      if (this.currentMode === 'mcc') {
        this.mccKeyword = newVal;
      } else {
        this.traditionalKeyword = newVal;
      }
    },
    '$route.query.account_id'() {
      this.loadCurrentAccountInfo();
    },
    '$route.query.credentialUserId'() {
      // 如果是 handleCredentialChanged 触发的路由变化，跳过重复加载
      if (this._isCredentialChanging) return;

      this.mccAccounts = [];
      this.traditionalAccounts = [];
      this.accountStats = {};
      this.mccTreeKey++;
      this.keyword = '';
      this.mccKeyword = '';
      this.traditionalKeyword = '';
      this.lastSearchKeyword = '';
      this.mccSearchLoading = false;

      // 清空缓存
      this.traditionalAccountsCache = { 1: [], 2: [] };
      this.traditionalPageCache = { 1: 1, 2: 1 };
      this.traditionalHasMoreCache = { 1: true, 2: true };

      this.loadData();
    },
  },
};
</script>

<style lang="scss" scoped>
.account-wrapper {
  @apply flex items-center w-48 h-7 text-sm text-white bg-blue-700 cursor-pointer rounded px-2;

  i {
    @apply mx-2;
  }

  i.el-icon-caret-bottom {
    @apply ml-auto;
  }
}

.f-circle,
.small-circle {
  @apply w-2 h-2 rounded-full;
}

.f-circle {
  @apply -top-0.5 -right-0.5;
}

.small-circle {
  @apply top-0.5 right-0.5;
}

.bg-success {
  @apply bg-green-500;
}

.bg-danger {
  @apply bg-red-500;
}

.violation-tag {
  flex-shrink: 0;
  margin-left: 6px;
  padding: 0 5px;
  height: 16px;
  line-height: 16px;
  font-size: 11px;
  color: #fff;
  background: #f56c6c;
  border-radius: 2px;
  white-space: nowrap;
}

// el-tree样式覆盖
::v-deep .el-tree {
  @apply bg-transparent;

  .el-tree-node {
    .el-tree-node__content {
      @apply items-start py-0 pr-2;
      height: auto;
      min-height: 50px;
      align-items: flex-start;
      margin: 2px 4px;
    }
  }
}
::v-deep .el-tabs__header {
  margin: 0;
}
// 加载中禁用 tab 切换的视觉反馈
::v-deep .tabs-disabled {
  .el-tabs__item {
    cursor: not-allowed;
    opacity: 0.5;
  }
}
</style>
